import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import LoadingOverlay from 'react-loading-overlay';
import ActiveDistributionHouseTableView from './components/ActiveDistributionHouseTableView';
import HighBalanceAgentTableView from './components/HighBalanceAgentTableView';
import DhStockBalanceLiftingTableView from './components/DhStockBalanceLiftingTableView';
import DhStockProgressTableView from './components/DhStockProgressTableView';
import DhStockBalanceAgentTableView from './components/DhStockBalanceAgentTableView';
import ActiveAgentTableView from './components/ActiveAgentTableView';
import AgentWithTransactionTableView from './components/AgentWithTransactionTableView';
import LeadingAgentTableView from './components/LeadingAgentTableView';
import LeadingAgentCategoryTableView from './components/LeadingAgentCategoryTableView';
import MilkingAgentTableView from './components/MilkingAgentTableView';
import { change_popup_details_view_state } from './actions';
import { getPopUpDetailsStatus } from './reducers';
import { getHelpSummary } from '../../reducers';
import {
  getFilteringState,
  getActiveDistributionHouses,
  getHighBalanceAgents,
  getDhStockBalanceLiftings,
  getDhStockProgress,
  getDhStockBalanceAgents,
  getActiveAgents,
  getActiveAgentsWithoutDisbursement,
  getActiveAgentsPartnerPayment,
  getAgentsWithTransaction,
  getLeadingAgents,
  getLeadingAgentsTpv,
  getLeadingGoldAgents,
  getLeadingSilverAgents,
  getLeadingBronzeAgents,
  getMilkingAgents,
  getDetailsViewLoadingState
} from '../reducers';
import { ToolbarOptions } from 'tubular-react';

const styles = theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    width: 'fit-content'
  },
  formControl: {
    marginTop: theme.spacing.unit * 2,
    minWidth: 120
  },
  formControlLabel: {
    marginTop: theme.spacing.unit
  }
});

const renderTableView = (filteringState, data = {}) => {
  if (filteringState.distribution_house)
    return (
      <ActiveDistributionHouseTableView
        list={data.active_distribution_houses}
        summary={data.help_summary}
      />
    );
  else if (filteringState.high_balance_agents)
    return (
      <HighBalanceAgentTableView
        list={data.high_balance_agents}
        summary={data.help_summary}
      />
    );
  else if (filteringState.dh_stock_balance_lifting)
    return (
      <DhStockBalanceLiftingTableView
        list={data.dh_stock_balance_liftings}
        summary={data.help_summary}
      />
    );
  else if (filteringState.dh_stock_progress)
    return (
      <DhStockProgressTableView
        list={data.dh_stock_progress}
        summary={data.help_summary}
      />
    );
  else if (filteringState.dh_stock_balance_agent)
    return (
      <DhStockBalanceAgentTableView
        list={data.dh_stock_balance_agents}
        summary={data.help_summary}
      />
    );
  else if (filteringState.active_agents)
    return <ActiveAgentTableView list={data.active_agents} />;
  else if (filteringState.active_agent_without_disbursement_cashout)
    return (
      <ActiveAgentTableView
        list={data.active_agents_without_disbursement.filter(
          agent => agent.transaction_info.without_disbursment_amount >= 5
        )}
      />
    );
  else if (filteringState.active_agent_partner_payment_only)
    return (
      <ActiveAgentTableView
        list={data.active_agents_partner_payment.filter(
          agent => agent.transaction_info.partner_payment >= 5
        )}
      />
    );
  else if (filteringState.agent_with_transaction)
    return (
      <AgentWithTransactionTableView list={data.agents_with_transaction} />
    );
  else if (filteringState.leading_agents)
    return <LeadingAgentTableView list={data.leading_agents} />;
  else if (filteringState.leading_agent_tpv)
    return <LeadingAgentTableView list={data.leading_agents_tpv} />;
  else if (filteringState.leading_agent_category)
    return (
      <LeadingAgentCategoryTableView
        list={[
          ...data.leading_gold_agents,
          ...data.leading_silver_agents,
          ...data.leading_bronze_agents
        ]}
      />
    );
  else if (filteringState.milking_agent)
    return <MilkingAgentTableView list={data.milking_agents} />;
};

const MaxWidthDialog = props => {
  const open = useSelector(getPopUpDetailsStatus);
  const filteringState = useSelector(getFilteringState);
  const active_distribution_houses = useSelector(getActiveDistributionHouses);
  const high_balance_agents = useSelector(getHighBalanceAgents);
  const dh_stock_balance_liftings = useSelector(getDhStockBalanceLiftings);
  const dh_stock_progress = useSelector(getDhStockProgress);
  const dh_stock_balance_agents = useSelector(getDhStockBalanceAgents);
  const active_agents = useSelector(getActiveAgents);
  const active_agents_without_disbursement = useSelector(
    getActiveAgentsWithoutDisbursement
  );
  const active_agents_partner_payment = useSelector(
    getActiveAgentsPartnerPayment
  );
  const agents_with_transaction = useSelector(getAgentsWithTransaction);
  const leading_agents = useSelector(getLeadingAgents);
  const leading_agents_tpv = useSelector(getLeadingAgentsTpv);
  const leading_gold_agents = useSelector(getLeadingGoldAgents);
  const leading_silver_agents = useSelector(getLeadingSilverAgents);
  const leading_bronze_agents = useSelector(getLeadingBronzeAgents);
  const milking_agents = useSelector(getMilkingAgents);
  const loading_state = useSelector(getDetailsViewLoadingState);
  const help_summary = useSelector(getHelpSummary);
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(change_popup_details_view_state(false));
  };

  return (
    <React.Fragment>
      <Dialog
        fullWidth={true}
        maxWidth={'md'}
        open={open}
        onClose={handleClose}
        aria-labelledby="max-width-dialog-title"
      >
        <LoadingOverlay
          active={loading_state}
          spinner
          text="Loading your content..."
        >
          {renderTableView(filteringState, {
            active_distribution_houses,
            high_balance_agents,
            dh_stock_balance_liftings,
            dh_stock_progress,
            dh_stock_balance_agents,
            active_agents,
            active_agents_without_disbursement,
            active_agents_partner_payment,
            agents_with_transaction,
            leading_agents,
            leading_agents_tpv,
            leading_gold_agents,
            leading_silver_agents,
            leading_bronze_agents,
            milking_agents,
            help_summary
          })}
          <DialogActions>
            <Button
              variant="contained"
              style={{ background: 'red', color: 'white', zIndex: 10000000 }}
              onClick={handleClose}
            >
              Close
            </Button>
          </DialogActions>
        </LoadingOverlay>
      </Dialog>
    </React.Fragment>
  );
};

MaxWidthDialog.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(MaxWidthDialog);
